import Intercom from '@intercom/messenger-js-sdk';

class IntercomService {
  constructor() {
    this.appId = 'cafc6co8';
    this.isInitialized = false;
    this.isChatEnabled = import.meta.env?.VITE_INTERCOM_ENABLED === 'true';
  }

  init(options = {}) {
    if (this.isInitialized) {
      console.warn('Intercom is already initialized');
      return;
    }

    try {
      Intercom({
        app_id: this.appId,
        alignment: options?.alignment || 'left',
        hide_default_launcher: !this.isChatEnabled,
      });
      this.isInitialized = true;
      console.log('Intercom initialized successfully', this.isChatEnabled ? '(chat enabled)' : '(chat hidden)');
    } catch (error) {
      console.error('Failed to initialize Intercom:', error);
    }
  }

  // Identify user with their information
  identifyUser(user) {
    if (!this.isInitialized) {
      console.warn('Intercom not initialized. Call init() first.');
      return;
    }

    try {
      Intercom({
        app_id: this.appId,
        user_id: user.id || user._id,
        email: user.email,
        name: user.name || user.fullName || `${user.firstName || ''} ${user.lastName || ''}`.trim(),
        intercom_user_jwt: user?.intercomJWTToken || null,
        created_at: user.createdAt ? Math.floor(new Date(user.createdAt).getTime() / 1000) : undefined,
        company: user.company || undefined,
        phone: user.phone || undefined,
        hide_default_launcher: !this.isChatEnabled,
        custom_attributes: {
          subscription_plan: user.subscriptionPlan || undefined,
          user_type: user.userType || undefined,
        }
      });

      console.log('User identified in Intercom:', user.email);
    } catch (error) {
      console.error('Failed to identify user in Intercom:', error);
    }
  }

  // Reset Intercom (useful for logout)
  reset() {
    if (!this.isInitialized) {
      return;
    }

    try {
      Intercom('shutdown');
      this.isInitialized = false;
      console.log('Intercom reset successfully');
    } catch (error) {
      console.error('Failed to reset Intercom:', error);
    }
  }

  // Show Intercom messenger (only if chat is enabled)
  show() {
    if (!this.isChatEnabled) {
      console.warn('Intercom chat is disabled via VITE_INTERCOM_ENABLED flag');
      return;
    }

    if (!this.isInitialized) {
      console.warn('Intercom not initialized. Call init() first.');
      return;
    }

    try {
      Intercom('show');
    } catch (error) {
      console.error('Failed to show Intercom:', error);
    }
  }

  // Hide Intercom messenger
  hide() {
    if (!this.isInitialized) {
      return;
    }

    try {
      Intercom('hide');
    } catch (error) {
      console.error('Failed to hide Intercom:', error);
    }
  }

  // Show specific message (only if chat is enabled)
  showMessage(message) {
    if (!this.isChatEnabled) {
      console.warn('Intercom chat is disabled via VITE_INTERCOM_ENABLED flag');
      return;
    }

    if (!this.isInitialized) {
      console.warn('Intercom not initialized. Call init() first.');
      return;
    }

    try {
      Intercom('showMessage', message);
    } catch (error) {
      console.error('Failed to show message in Intercom:', error);
    }
  }

  // Show new message (only if chat is enabled)
  showNewMessage(message) {
    if (!this.isChatEnabled) {
      console.warn('Intercom chat is disabled via VITE_INTERCOM_ENABLED flag');
      return;
    }

    if (!this.isInitialized) {
      console.warn('Intercom not initialized. Call init() first.');
      return;
    }

    try {
      Intercom('showNewMessage', message);
    } catch (error) {
      console.error('Failed to show new message in Intercom:', error);
    }
  }

  // Track event (always works regardless of chat visibility)
  trackEvent(eventName, metadata = {}) {
    if (!this.isInitialized) {
      console.warn('Intercom not initialized. Call init() first.');
      return;
    }

    try {
      Intercom('trackEvent', eventName, metadata);
    } catch (error) {
      console.error('Failed to track event in Intercom:', error);
    }
  }

  // Check if chat widget is enabled
  isChatWidgetEnabled() {
    return this.isChatEnabled;
  }
}

const intercomService = new IntercomService();
export default intercomService;